import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { Errors } from "@/errors";
import type { SearchExecutionActivity } from "@/lib/search-activity";

type FailurePresentation = {
  description: string;
  showUpgradeLink: boolean;
};

export const GENERIC_FAILURE_MESSAGE =
  "Something went wrong while running this search. Our team has been notified and will look into it shortly.";

function getFailurePresentation(
  failureReason: SearchExecutionActivity["failureReason"],
): FailurePresentation {
  switch (failureReason) {
    case Errors.INSUFFICIENT_CREDITS:
      return {
        description:
          "Your account does not have enough credits to run this search. Upgrade to a paid plan to continue.",
        showUpgradeLink: true,
      };
    case Errors.NO_ACTIVE_PROMPTS:
      return {
        description:
          "This search cannot run because it does not have any active prompts. Add or enable at least one prompt and try again.",
        showUpgradeLink: false,
      };
    case Errors.NO_ACTIVE_LLMS:
      return {
        description:
          "This search cannot run because it does not have any active models. Add or enable at least one model and try again.",
        showUpgradeLink: false,
      };
    default:
      return {
        description: GENERIC_FAILURE_MESSAGE,
        showUpgradeLink: false,
      };
  }
}

export function SearchSpecificationFailureBanner({
  activity,
}: {
  activity: SearchExecutionActivity | null | undefined;
}) {
  if (!activity || (activity.status !== "FAILED" && activity.status !== "ABANDONED")) {
    return null;
  }

  const { description, showUpgradeLink } = getFailurePresentation(activity.failureReason);

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-destructive/15 bg-destructive/[0.04] px-3.5 py-2.5 text-xs">
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-3 w-3 text-destructive" />
      </div>
      <p className="min-w-0">
        <span className="font-semibold text-destructive">
          {activity.status === "ABANDONED"
            ? "The latest search run was interrupted."
            : "The latest search run failed."}
        </span>
        <span className="ml-1.5 text-muted-foreground">{description}</span>
        {showUpgradeLink ? (
          <Link
            to="/billing"
            className="ml-1.5 inline-flex font-medium text-destructive underline decoration-destructive/30 underline-offset-2 transition-colors hover:decoration-destructive/60"
          >
            Upgrade to a paid plan
          </Link>
        ) : null}
      </p>
    </div>
  );
}
